/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// mantine
import {
	ActionIcon,
	Badge,
	Box,
	Paper,
} from '@mantine/core';

// styles
import classes from './producers.module.css';

// components

// i18n
import { useTranslation } from 'react-i18next';

import { useProducersApi } from '@features/resources/producers/api/get-producers';
import { useRemove } from '@features/resources/producers/api/remove-producer';

// configs

// utils
import { useProducers } from '@features/resources/producers/hooks/useProducers';

import { Button } from '@components/v2/ui';
import { Datatable } from '@components/v2/ui/Datatable/Datatable';
import { Pagination } from '@components/v2/ui/Pagination';
import { ALL_COUNTRIES } from '@config/constants/all-countries';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';
import { useDisclosure } from '@mantine/hooks';
import {
	IconFilter,
	IconPlus,
} from '@tabler/icons-react';
import { isEmpty } from 'lodash';
import isNil from 'lodash/isNil';
import { useCountryCodes } from 'shared/api/get-country-codes';
import { useOrganizationsHasProducers } from 'shared/api/get-organizations-has-entity-producer';

// configs
import { Details } from '@features/resources/producers/components/Details';
import { Filter } from '@features/resources/producers/components/Filter';
import { tableSchemaBase as columns } from '@features/resources/producers/producers.columns';
import { tableSchemaBase as columnsForPartner } from '@features/resources/producers/producersForPartner.columns';
import { tableSchemaBase as columnsWithoutContact } from '@features/resources/producers/producersWithoutContact.columns';

import { ACLS } from '@config/constants/acls';
import { TTargetColumn } from '@global-types/index';
import useStore from '@store/index';
import { checkAclFront } from '@utils/acl-front';
import { getVisibleColumns } from '@utils/functions';
import { useState } from 'react';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const Producers = () => {
	const { t } = useTranslation();
	const {
		producersForPartnerDatatableOptions,
		setProducersForPartnerDatatableOptions,
	} = useStore();

	const {
		producersDatatableOptions,
		setProducersDatatableOptions,
		openDrawer,
		handleRowClicked,
		handleSortStatusChange,
		handlepaginationchangesize,
		handlechangepagination,
		producersPage,
		producersPageSize,
		producersQueryParams,
		producersSortStatus,
		producersSortParams,
		producersSelectedRow,
		setProducersWithoutContactDatatableOptions,
		producersWithoutContactDatatableOptions,
		setProducersWithoutOrganizationDatatableOptions,
		producersWithoutOrganizationDatatableOptions,
		setProducersSelectedRow,
		setOpenDrawer,
	} = useProducers();

	const [
		openedFilters,
		{ open: openFilters, close: closeFilters },
	] = useDisclosure(false);

	// APIs
	const { data: organizations } =
		useOrganizationsHasProducers();

	const {
		data,
		isLoading,
		total: totalItems,
		totalPages,
	} = useProducersApi(
		producersPage,
		producersPageSize,
		producersQueryParams,
		producersSortParams
	);

	const remove = useRemove(
		producersPage,
		producersPageSize
	);

	const { data: countryCodes } =
		useCountryCodes('producers');

	const filteredCountries =
		!isNil(countryCodes) && Array.isArray(countryCodes)
			? ALL_COUNTRIES.filter((item) =>
					countryCodes.includes(item.value)
				)
			: [];

	const { checkAcls, checkRole, role } =
		useAuthorization();

	const [filtersNbr, setFiltersNbr] = useState(0);

	return (
		<Paper className={classes['main-layout']}>
			<Details
				opened={
					!isEmpty(producersSelectedRow) && openDrawer
				}
				data={producersSelectedRow}
				onClose={() => {
					setOpenDrawer(false);
					setProducersSelectedRow({});
				}}
			/>
			{checkAclFront(
				ACLS.FRONT.PRODUCERS.FILTER,
				role as ROLES
			) && (
				<Filter
					opened={openedFilters}
					onClose={closeFilters}
					title={t('filters.titles.producers')}
					onApplyFilters={(filters) => {
						setFiltersNbr(Object.keys(filters).length);
					}}
					value={producersQueryParams}
				/>
			)}
			<Box className={classes['action-bar']}>
				{checkAclFront(
					ACLS.FRONT.PRODUCERS.FILTER,
					role as ROLES
				) && (
					<div
						style={{
							position: 'relative',
						}}
					>
						{filtersNbr > 0 && (
							<Badge
								style={{
									position: 'absolute',
									zIndex: '3',
									right: '-3px',
									fontFamily: 'InterDisplay-Regular',
								}}
								color="var(--mantine-color-igluRed-9)"
								size="xs"
								circle
							>
								{filtersNbr}
							</Badge>
						)}
						<ActionIcon
							variant="outline"
							size="48px"
							radius="xl"
							color={'var(--mantine-color-igluBorder-9)'}
							data-testid="bell-icon-button"
							onClick={openFilters}
							bg={
								!isEmpty(producersQueryParams)
									? 'var(--mantine-color-igluPrimary-9)'
									: 'transparent'
							}
						>
							<IconFilter
								color={
									!isEmpty(producersQueryParams)
										? 'var(--mantine-color-igluTextButton-9)'
										: 'var(--mantine-color-igluPrimary-9)'
								}
								radius="xl"
								size={24}
								stroke={1.5}
							/>
						</ActionIcon>
					</div>
				)}

				{checkAcls([ACLS.PRODUCERS.CREATE]) && (
					<Button
						disabled
						leftSection={
							<IconPlus
								radius="xl"
								size={24}
								stroke={1.5}
							/>
						}
						variant={'primary'}
					>
						{t('producers.addProducer')}
					</Button>
				)}
			</Box>

			<Box className={classes['container-table']}>
				{Array.isArray(data) && (
					<Datatable
						fetching={isLoading}
						onRowClick={handleRowClicked}
						handleUpdateColumns={(
							cols: TTargetColumn[]
						) => {
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? setProducersDatatableOptions(
										getVisibleColumns(columns, cols)
									)
								: checkRole([ROLES.ORGANIZATION_ADMIN])
									? setProducersForPartnerDatatableOptions(
											getVisibleColumns(
												columnsForPartner,
												cols
											)
										)
									: setProducersWithoutContactDatatableOptions(
											getVisibleColumns(
												columnsWithoutContact,
												cols
											)
										);
						}}
						allColumns={
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? columns
								: checkRole([ROLES.ORGANIZATION_ADMIN])
									? columnsForPartner
									: columnsWithoutContact
						}
						columns={
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? producersDatatableOptions
								: checkRole([ROLES.ORGANIZATION_ADMIN])
									? producersForPartnerDatatableOptions
									: producersWithoutContactDatatableOptions
						}
						records={data}
						onSortStatusChange={handleSortStatusChange}
						sortstatus={producersSortStatus}
					/>
				)}
			</Box>

			<Box className={classes['pagination-bar']}>
				<Pagination
					pageSize={producersPageSize}
					totalItems={totalItems}
					activePage={producersPage}
					onPageSizeChange={handlepaginationchangesize}
					onChange={handlechangepagination}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Paper>
	);
};

export default Producers;
